import { useState, useRef, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Sparkles, Send, Loader2, RotateCcw } from "lucide-react";

type Msg = { role: "user" | "assistant"; content: string };

const SUGGESTIONS = [
  "Hey, do you do move-out cleans? 3 bed, 2 bath in Riverside.",
  "How much for a full detail on a mid-size SUV?",
  "Need a quote to power-wash a 2-story house driveway + siding.",
];

export default function WatchSolChat() {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text: string) => {
    const clean = text.trim();
    if (!clean || loading) return;
    const next: Msg[] = [...messages, { role: "user", content: clean }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("sol-demo", {
        body: { messages: next },
      });
      if (error) throw error;
      const reply = data?.reply ?? "Sorry — try again in a moment.";
      setMessages([...next, { role: "assistant", content: reply }]);
    } catch (e: any) {
      setMessages([
        ...next,
        { role: "assistant", content: "I'm having trouble responding right now. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setInput("");
  };

  return (
    <div className="rounded-2xl bg-card border border-border shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/40">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="font-semibold text-foreground text-sm">Sol · Live demo</div>
            <div className="text-xs text-muted-foreground">Talk to Sol like a customer would</div>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={reset}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <RotateCcw className="h-3 w-3" /> Reset
          </button>
        )}
      </div>

      <div ref={scrollRef} className="p-6 space-y-4 min-h-[320px] max-h-[420px] overflow-y-auto">
        {messages.length === 0 && (
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              Try one of these
            </p>
            <div className="flex flex-col gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-left text-sm rounded-xl border border-border bg-background hover:bg-muted/50 hover:border-primary/40 px-4 py-3 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "" : "justify-end"}`}>
            {m.role === "assistant" ? (
              <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary text-primary-foreground px-4 py-3 text-sm shadow-md whitespace-pre-wrap">
                <div className="text-[10px] font-bold tracking-widest opacity-80 mb-1">
                  SOL · DRAFT
                </div>
                {m.content}
              </div>
            ) : (
              <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-muted px-4 py-3 text-sm text-foreground whitespace-pre-wrap">
                {m.content}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex justify-end">
            <div className="rounded-2xl rounded-tr-sm bg-primary/80 text-primary-foreground px-4 py-3 text-sm shadow-md inline-flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Sol is composing…
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
        className="border-t border-border bg-background p-3 flex items-center gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Message Sol as if you were a customer…"
          disabled={loading}
          className="flex-1 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-primary text-primary-foreground disabled:opacity-40 hover:opacity-90 transition"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
